
// getFeatureFlag :: String -> userModel -> Boolean
export const getFeatureFlag = flag => userModel => userModel.debugModel.getFeatureFlag(flag);

export class Modules {
  constructor() {
    this._baseModules = {};
    this._liveModules = {};
    this._modules = {};
    this._listeners = {};
  }

  initialize({baseModules = {}, appContext, dependencies = {}} = {}) {
    const {ramda: {
      forEach,
      indexBy,
      map,
      mapObjIndexed,
      merge,
      prop,
      values,
      zipObj,
    }} = dependencies;

    this._baseModules = indexBy(prop('id'), values(
      mapObjIndexed((mod, key) => merge(mod, {id: mod.id || key}),
        baseModules
      )
    ));

    this._liveModules = zipObj(Object.keys(this._baseModules), map(Boolean, Object.keys(this._baseModules)));
    this._modules = {};
    this._dependencies = dependencies;

    forEach(
      (dependency, key) =>
        Object.defineProperty(this, key, {get: () => this._dependencies[key]}),
      this._dependencies
    );

    return this.loadModulesForUser(appContext);
  }

  registerModule(mod, opts = {}, id = opts.id || mod.id, hide = opts.hide || false) {
    this._baseModules[id] = this._dependencies.ramda.mergeAll([mod, opts, {id}]);
    this._liveModules[id] = !hide;

    // TODO: Serialize this decision maybe? - ADM 12.27.16

    return this.loadModulesForUser();
  }

  showModule(id) {
    if (!this._baseModules[id]) {
      return Promise.reject(new Error(`Module ${id} does not exist`));
    }

    this._liveModules[id] = true;
    return this.loadModulesForUser();
  }

  hideModule(id) {
    this._liveModules[id] = false;
    return this.loadModulesForUser();
  }

  removeModule(id) {
    this._liveModules[id] = false;
    delete this._baseModules[id];

    return this.loadModulesForUser();
  }

  getModules() {
    const {ramda: {
      map,
      merge,
      values,
    }} = this._dependencies;

    return map((module) => merge({
      isLive: this._liveModules[module.id],
      userHasPermission: module.isAllowedForUser(this._appContext),
    }, module), values(this._baseModules));
  }

  listenForModuleUpdates(listener, name = 'listener.' + Object.keys(this._listeners).length) {
    this._listeners[name] = listener;
  }

  removeListener(name) {
    delete this._listeners[name];
  }

  loadModulesForUser(appContext = this._appContext) {
    const {ramda: {
      filter,
      forEach,
      pipe,
      propSatisfies,
      values,
    }} = this._dependencies;

    this._appContext = appContext;

    // Filter for live modules with compatible permissions
    this._modules = values(filter((module) =>
      this._liveModules[module.id] && module.isAllowedForUser(appContext), this._baseModules));

    // (Re)initialize any modules with explicit initialization hooks
    this._modules
      .filter(propSatisfies(init => typeof init === 'function', 'initializeModule'))
      .forEach(module =>
        module.initializeModule(this.getComponentsForModule(module), this._dependencies)
      );

    pipe(
      values,
      forEach(listener => typeof listener === 'function' && listener(this))
    )(this._listeners);

    return Promise.resolve(this._modules);
  }

  getModuleButtons() {
    const {ramda: {
      chain,
      pipe,
      propOr,
      sortBy,
    }} = this._dependencies;

    return pipe(
      chain(module => module.getModuleButtons()),
      sortBy(propOr(Infinity, 'priority'))
    )(this._modules);
  }

  // .getVisibleModuleButtons :: (appContext, String) -> ModuleButton
  getVisibleModuleButtons(appContext, loc){
    return this.getModuleButtons().filter(
      ({checkVisible = this._dependencies.ramda.T, location}) => loc === location && checkVisible(appContext)
    );
  }

  getModuleRoutes(Backbone, userModel, appContext) {
    const {ramda: {
      filter,
      allPass,
      identity,
      prop,
      map,
    }} = this._dependencies;

    return filter(
      allPass([identity, prop('route')]),
      map(module => module.getModuleRoute(Backbone, userModel, appContext), this._modules)
    );
  }

  setModuleRoutes(Backbone, userModel, appContext, router) {
    this._dependencies.ramda.forEach(
      module => module.setModuleRoute({Backbone, userModel, appContext, router}),
      this._modules
    );
  }

  getSiteMapActions() {
    return this._dependencies.ramda.chain(module => module.getModuleActions(), this._modules);
  }

  getComponentsForModule(mod = false) {
    const internal = this._baseModules[mod.id || mod] || mod;
    return (internal && internal.componentTransform) ?
      this._dependencies.ramda.map(internal.componentTransform, this._modules) : this._modules;
  }

  getDependencies(name) {
    return name ? this._dependencies[name] : {...this._dependencies};
  }
}

export default Modules;
